import { Menu, PanelLeftClose, PanelLeftOpen, Plus, ShoppingCart } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import { useAppSelector } from './hooks';
import { Button } from '../components/Button';
import { RoundTimersIndicator } from '../features/currentEvents/RoundTimersIndicator';
import { useMobileApp } from '../hooks/useMobileApp';
import { ThemeToggle } from './ThemeToggle';

// Lazy so the tickets feature chunk isn't pulled into the shell bundle just
// for a button that most renders never click.
const TicketFormDialog = lazy(() =>
    import('../features/tickets/components/TicketFormDialog').then((m) => ({
        default: m.TicketFormDialog,
    })),
);

interface HeaderProps {
    onToggleSidebar: () => void;
    /** Desktop (lg+) nav-rail collapse state + toggle. */
    collapsed?: boolean;
    onToggleCollapse?: () => void;
}

export function Header({ onToggleSidebar, collapsed = false, onToggleCollapse }: HeaderProps) {
    const level = useAppSelector(
        (state) => (state as any).authentication?.user?.role?.level ?? 0,
    );
    // Non-admins on mobile live entirely in v2 (Tickete + Rapoarte): no POS pill,
    // no escape hatch back to the classic (v1) app.
    const restricted = useMobileApp() && level < 3;
    const [ticketOpen, setTicketOpen] = useState(false);

    return (
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-solid border-gray-200 bg-white px-3 dark:border-gray-700 dark:bg-gray-800 sm:gap-3 sm:px-4">
            <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={onToggleSidebar}
                aria-label="Deschide meniul"
            >
                <Menu aria-hidden="true" className="h-5 w-5" />
            </Button>
            {onToggleCollapse && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="hidden shrink-0 lg:inline-flex"
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? 'Afiseaza meniul' : 'Ascunde meniul'}
                    aria-pressed={collapsed}
                >
                    {collapsed ? (
                        <PanelLeftOpen aria-hidden="true" className="h-5 w-5" />
                    ) : (
                        <PanelLeftClose aria-hidden="true" className="h-5 w-5" />
                    )}
                </Button>
            )}
            <div className="flex shrink-0 items-center gap-2">
                <img src="/logo192.png" alt="" className="h-7 w-7 shrink-0" />
                <span className="whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">
                    KRIT Orchestrator
                </span>
            </div>
            <div className="flex-1" />
            <RoundTimersIndicator />
            {/* Everyone who can access tickets (level >= 2, Staff/Shop included)
                can raise one from anywhere — including restricted mobile mode,
                where ticket capture is the main job. */}
            {level >= 2 && (
                <Button variant="secondary" size="sm" className="shrink-0" onClick={() => setTicketOpen(true)}>
                    <Plus aria-hidden="true" className="h-5 w-5 shrink-0" />
                    <span className="hidden sm:inline">Ticket nou</span>
                </Button>
            )}
            {level >= 2 && !restricted && (
                <Button asChild variant="secondary" size="sm" className="shrink-0">
                    <Link to="/v2/pos" aria-label="Deschide POS">
                        <ShoppingCart aria-hidden="true" className="h-5 w-5 shrink-0" />
                        <span className="hidden sm:inline">POS</span>
                    </Link>
                </Button>
            )}
            <ThemeToggle />
            {!restricted && (
                <a
                    href="/v1/dashboard"
                    className="hidden text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 md:inline"
                >
                    Inapoi la versiunea clasica
                </a>
            )}
            {ticketOpen && (
                <Suspense fallback={null}>
                    <TicketFormDialog open={ticketOpen} onOpenChange={setTicketOpen} ticket={null} />
                </Suspense>
            )}
        </header>
    );
}
